import { useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { FormControlLabel, Switch, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";
import AlertDialog from "./alertDialog";

const formatObjectField = value => {
  if (value == null) {
    return "";
  }

  if (typeof value === "object" && Object.keys(value).length === 0) {
    return "";
  }

  return JSON.stringify(value, null, 2);
};

const parseObjectField = (value, fallbackValue = {}) => {
  if (value == null || value.trim() === "") {
    return fallbackValue;
  }

  try {
    return JSON.parse(value);
  } catch {
    return fallbackValue;
  }
};

const parseArrayField = value => {
  if (value == null || value.trim() === "") {
    return [];
  }

  try {
    const parsedValue = JSON.parse(value);

    if (Array.isArray(parsedValue)) {
      return parsedValue;
    }

    return [parsedValue];
  } catch {
    return value
      .split("\n")
      .map(item => item.trim())
      .filter(Boolean);
  }
};

const cleanProductRecord = productRecord => {
  const { computed, stale, name, description, price, state, ...cleanProductRecord } =
    productRecord || {};

  return cleanProductRecord;
};

const BotpressProductsDialog = props => {
  const {
    open,
    productRecord,
    onClose,
    onAccept,
    onDelete,
    dialogTitle = "Edit Product",
    okMessage = "Ok",
    cancelMessage = "Cancel",
    maxWidth = "md",
    fullWidth = true,
    deleteMessage = "Delete",
  } = props;

  const theme = useTheme();

  const [type, setType] = useState(productRecord.type || "");
  const [archived, setArchived] = useState(productRecord.archived || false);
  const [title, setTitle] = useState(productRecord.title || "");
  const [summary, setSummary] = useState(productRecord.summary || "");
  const [startDate, setStartDate] = useState(productRecord.startDate || "");
  const [endDate, setEndDate] = useState(productRecord.endDate || "");
  const [cta, setCta] = useState(formatObjectField(productRecord.cta));
  const [assets, setAssets] = useState(formatObjectField(productRecord.assets));

  // Metadata is commented out for now because the Botpress table definition
  // currently rejects arbitrary metadata keys.
  // const [metadata, setMetadata] = useState(formatObjectField(productRecord.metadata));

  const [matchSignals, setMatchSignals] = useState(formatObjectField(productRecord.matchSignals));
  const [softRequirements, setSoftRequirements] = useState(
    formatObjectField(productRecord.softRequirements),
  );
  const [showConfirm, setShowConfirm] = useState(false);

  const onDeleteHandler = () => {
    onDelete(productRecord);
  };

  const onAcceptHandler = () => {
    try {
      const editedProductRecord = cleanProductRecord({
        ...productRecord,
        type: type.trim(),
        archived: archived,
        title: title.trim(),
        summary: summary.trim(),
        startDate: startDate.trim() || null,
        endDate: endDate.trim() || null,
        cta: parseObjectField(cta, {}),
        assets: parseObjectField(assets, {}),
        metadata: productRecord.metadata ?? null,
        matchSignals: parseArrayField(matchSignals),
        softRequirements: parseArrayField(softRequirements),
      });

      onAccept(editedProductRecord);
    } catch (error) {
      console.log(error);
    }
  };

  const onCloseHandler = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onCloseHandler();
        }
      }}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>

      <DialogContent>
        <Grid spacing={1} container size={{ xs: 12 }}>
          {productRecord.id != null && (
            <Grid item size={{ xs: 12 }}>
              <Typography variant="body2" color="textSecondary">
                Product ID: {productRecord.id}
              </Typography>
            </Grid>
          )}

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Title"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productTitle"}
              value={title}
              onChange={event => setTitle(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Summary"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productSummary"}
              rowCount={4}
              multiline={true}
              value={summary}
              onChange={event => setSummary(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Type"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productType"}
              value={type}
              onChange={event => setType(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 6 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Start Date"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productStartDate"}
              value={startDate}
              onChange={event => setStartDate(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 6 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"End Date"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productEndDate"}
              value={endDate}
              onChange={event => setEndDate(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={archived}
                  onChange={event => setArchived(event.target.checked)}
                  color="secondary"
                />
              }
              label={"Archived"}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"CTA"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productCta"}
              rowCount={4}
              multiline={true}
              value={cta}
              onChange={event => setCta(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Assets"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productAssets"}
              rowCount={4}
              multiline={true}
              value={assets}
              onChange={event => setAssets(event.target.value)}
            />
          </Grid>

          {/*
          Metadata is hidden for now until the Integration/Table definition
          supports the allowed metadata fields.

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Metadata"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productMetadata"}
              rowCount={4}
              multiline={true}
              value={metadata}
              onChange={event => setMetadata(event.target.value)}
            />
          </Grid>
          */}

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Match Signals"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productMatchSignals"}
              rowCount={3}
              multiline={true}
              value={matchSignals}
              onChange={event => setMatchSignals(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Soft Requirements"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productSoftRequirements"}
              rowCount={3}
              multiline={true}
              value={softRequirements}
              onChange={event => setSoftRequirements(event.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Box
          sx={{
            paddingRight: theme.spacing(2),
            paddingBottom: 0,
            display: "flex",
            flexDirection: "row",
            userSelect: "none",
            webkitUserSelect: "none",
            position: "relative",
            flexGrow: 1,
          }}
        >
          {productRecord.id != null && props.onDelete != null && (
            <OmniResponsiveButton
              icon={<DeleteIcon />}
              onClick={() => setShowConfirm(true)}
              buttonText={deleteMessage}
              tooltipTitle=""
            />
          )}
        </Box>

        <Box
          sx={{
            flexGrow: 0,
            flexShrink: 1,
          }}
        >
          {cancelMessage && (
            <Button
              onClick={onCloseHandler}
              color="secondary"
              variant="contained"
              sx={{ marginRight: theme.spacing(2) }}
            >
              {cancelMessage}
            </Button>
          )}

          <Button onClick={onAcceptHandler} color="secondary" variant="contained">
            {okMessage}
          </Button>
        </Box>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete this Product?"}
          onAccept={() => {
            setShowConfirm(false);
            onDeleteHandler();
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}
    </Dialog>
  );
};

export default BotpressProductsDialog;
